import {
  Box,
  Divider,
  Grid2,
  Stack,
  Typography,
} from "@mui/material";
import {
  FileFragmentFragment,
  ZyusetuFieldSectionFragmentFragment,
} from "@/graphql/graphql";
import {DataGridItemHorizontal} from "@/components/dataDisplay/DataGridItemHorizontal";
import {Copy, Focus} from "lucide-react";
import {useContext, useEffect} from "react";
import {SetHighlightedField} from "@/hooks/use-highlight-field";
import {useGetFirstBoundingRegions, useGetFirstContent} from "@/hooks/useGetFirstContent";
import {useGetFormattedValue} from "@/hooks/use-get-formatted-value";
import toast from "react-hot-toast";

interface AnalyzeResultProps {
  sections: ZyusetuFieldSectionFragmentFragment[];
  file: FileFragmentFragment;
  copyValue?: boolean
}

export function AnalyzeResult({sections, file, copyValue}: AnalyzeResultProps) {
  const setHighlightedValue = useContext(SetHighlightedField);
  const getFirstContent = useGetFirstContent();
  const getBoundingRegions = useGetFirstBoundingRegions();
  const getFormattedValue = useGetFormattedValue();
  
  useEffect(() => {
    setHighlightedValue({
      field: undefined,
      file: file,
    });
  }, [file, setHighlightedValue]);
  
  return (
    <>
      {sections.map((section) => (
        <Grid2 container mb={3} spacing={2} key={`${section.id}`}>
          <Grid2 size={12}>
            <Typography
              variant="subtitle2"
              mb={1}
              sx={{
                pl: 1,
                borderLeft: "4px solid",
                borderColor: "primary.main",
                fontWeight: "bold",
              }}
            >
              {section.title}
            </Typography>
          </Grid2>
          {section.keys.sort(
            (a, b) => a.keyStyle.index - b.keyStyle.index,
          ).map((key) => {
            if (key.keyStyle.isSpace) {
              return (
                <Grid2 size={12} key={`${key.id}`}>
                  <Divider sx={{my: 1}}/>
                </Grid2>
              );
            }
            const boundingRegion = getBoundingRegions(key.analyzedPayloads);
            
            const hasBoundingRegion = boundingRegion?.pageAnchor ||
              boundingRegion?.boundingRegions?.[0]
            
            const value = getFormattedValue(
              key,
              getFirstContent(key.analyzedPayloads),
            )
            return (
              <DataGridItemHorizontal
                size={key.keyStyle.grid ?? 6}
                label={<Stack direction={"row"} gap={1}>
                  {key.label}
                  {(hasBoundingRegion) && (
                    <Focus size={"20px"}/>
                  )}
                  {copyValue && !!value && <Copy size={"20px"}/>}
                </Stack>}
                key={`${key.id}`}
              >
                <Box
                  sx={{
                    cursor: value && "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    minHeight: "20px",
                    whiteSpace: "pre-wrap",
                  }}
                  onClick={() => {
                    if (hasBoundingRegion) {
                      setHighlightedValue({
                        field: boundingRegion?.payload ?? undefined,
                        file: file,
                      });
                    }
                    if (copyValue && value) {
                      navigator.clipboard.writeText(value);
                      toast.success("コピーしました");
                    }
                  }}
                >
                  {value}
                </Box>
              </DataGridItemHorizontal>
            );
          })}
        </Grid2>
      ))}
    </>
  );
}
